import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import type { InviteCreate, InviteCreated, InviteRead, MemberRead } from '../../types/api';

function base(businessId: string): string {
  return `/api/v1/businesses/${businessId}`;
}

/** GET /businesses/{id}/members: everyone on the brand. Any member may read it. */
export function fetchMembers(businessId: string, signal?: AbortSignal): Promise<MemberRead[]> {
  return apiClient.get<MemberRead[]>(`${base(businessId)}/members`, { signal });
}

/** GET /businesses/{id}/invites: the pending invites. Owner only. */
export function fetchInvites(businessId: string, signal?: AbortSignal): Promise<InviteRead[]> {
  return apiClient.get<InviteRead[]>(`${base(businessId)}/invites`, { signal });
}

/**
 * POST /businesses/{id}/invites. Owner only. 402 `PLAN_LIMIT_REACHED`
 * (`team_members`) when the seats are full, 409 `INVITE_EXISTS` for an address
 * already invited or already on the brand. The `accept_url` comes back here
 * only: the list never carries it.
 */
export function createInvite(businessId: string, email: string): Promise<InviteCreated> {
  const body: InviteCreate = { email };
  return apiClient.post<InviteCreated>(`${base(businessId)}/invites`, { body });
}

/** DELETE /businesses/{id}/invites/{invite_id}: the link stops working. 204. */
export function revokeInvite(businessId: string, inviteId: string): Promise<void> {
  return apiClient.delete<void>(`${base(businessId)}/invites/${inviteId}`);
}

/** DELETE /businesses/{id}/members/{user_id}. Owner only, never the owner. 204. */
export function removeMember(businessId: string, userId: string): Promise<void> {
  return apiClient.delete<void>(`${base(businessId)}/members/${userId}`);
}

export interface TeamResult {
  /** Everyone not removed, owner first. Empty until the first load completes. */
  members: MemberRead[];
  /** Pending invites; always empty for a member, who cannot read them. */
  invites: InviteRead[];
  isLoading: boolean;
  error: string | null;
  /** Re-reads both lists. */
  reload: () => void;
}

interface Loaded {
  key: string;
  members: MemberRead[];
  invites: InviteRead[];
  error: string | null;
}

/** Owner first, then by who joined first. Removed rows hold no seat and are not shown. */
function visibleMembers(members: readonly MemberRead[]): MemberRead[] {
  return members
    .filter((member) => member.status !== 'removed')
    .sort((a, b) => {
      if (a.role !== b.role) return a.role === 'owner' ? -1 : 1;
      return a.joined_at.localeCompare(b.joined_at);
    });
}

/**
 * The brand's members and, for its owner, the pending invites.
 *
 * Tagged with the business (and the reload count) it was read for, so a brand
 * switch never shows one brand's team under another's heading.
 */
export function useTeam(businessId: string, isOwner: boolean): TeamResult {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [nonce, setNonce] = useState(0);
  const key = `${businessId}:${isOwner ? 'owner' : 'member'}`;

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const [members, invites] = await Promise.all([
          fetchMembers(businessId, controller.signal),
          isOwner ? fetchInvites(businessId, controller.signal) : Promise.resolve([]),
        ]);
        if (cancelled) return;
        setLoaded({ key, members: visibleMembers(members), invites, error: null });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setLoaded({
          key,
          members: [],
          invites: [],
          error: getErrorMessage(caught, 'Could not read the team.'),
        });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [businessId, isOwner, key, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);
  const isCurrent = loaded !== null && loaded.key === key;

  return {
    members: isCurrent ? loaded.members : [],
    invites: isCurrent ? loaded.invites : [],
    isLoading: !isCurrent,
    error: isCurrent ? loaded.error : null,
    reload,
  };
}
